import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ExternalLink, Smartphone, Apple, Chrome, RefreshCw, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { authAPI, adminAPI } from '../services/api';
import { getVersionString } from '../version';

interface CapturedAd {
  id: number;
  timestamp: string;
  userId: string;
  platform: string;
  eventType: string;
  confidence: string;
  adId: string | null;
  contentId: string | null;
  advertiserDomain: string | null;
  advertiserName: string | null;
  advertiserUrl: string | null;
  durationSec: number | null;
  url: string;
  ruleName: string;
  adTitle: string | null;
  adDescription: string | null;
  campaign: string | null;
  source: string | null;
  medium: string | null;
  content: string | null;
  meta: any;
  memberCode: string | null;
  memberNotes: string | null;
  userCountryCode: string | null;
  devicePlatform: string | null;
  userLabel: string;
  metadataStatus: string | null;
  metadataSiteName: string | null;
  metadataDescription: string | null;
  metadataImage: string | null;
  creativeUrl?: string | null;
  slotType?: string | null;
  adEventType?: string | null;
  adVideoCpn?: string | null;
  contentCpn?: string | null;
  watchTimeMs?: number | null;
  relatedAd?: {
    adTitle?: string | null;
    advertiserDomain?: string | null;
    advertiserUrl?: string | null;
    advertiserName?: string | null;
    metadataImage?: string | null;
    contentId?: string | null;
     creativeUrl?: string | null;
    timestamp?: string | Date | null;
  } | null;
  landingUrl?: string | null;
  enrichmentStatus: string | null;
  lastEnrichmentError: string | null;
  enrichedAt: string | null;
  category: string | null;
  categoryConfidence: number | null;
  classifiedAt: string | null;
  enrichedAdvertiserName: string | null;
  enrichedAdvertiserCategory: string | null;
  enrichedAdTitle: string | null;
  enrichedAdDescription: string | null;
  enrichedThumbnailUrl: string | null;
  enrichedLandingPageUrl: string | null;
  enrichedLandingPageTitle: string | null;
  enrichedLandingPageDescription: string | null;
  enrichedLandingPageImage: string | null;
  platformMetadata: any;
  enrichmentSource: string | null;
  enrichmentConfidence: string | null;
  sourceCountry: string | null;
  sourceCountryCode: string | null;
  isExcluded: boolean | null;
  exclusionReason: string | null;
  isManuallyClassified: boolean | null;
  manualClassifiedBy: string | null;
  manualClassifiedAt: string | null;
  originalCategory: string | null;
  originalCategoryConfidence: number | null;
}

interface AdStats {
  totalAds: string;
  totalPlatforms: string;
  totalUsers: string;
  totalAdvertisers: string;
  youtubeAds: string;
  facebookAds: string;
  instagramAds: string;
  xTwitterAds: string;
  impressions: string;
  clicks: string;
  conversions: string;
  enrichedAds: string;
  classifiedAds: string;
  gamblingAds: string;
  gamblingPachinko: string;
  gamblingOnline: string;
  gamblingPublic: string;
  gamblingGame: string;
  gamblingGachaPremium: string;
  firstAdDate: string;
  lastAdDate: string;
}

export default function CapturedAds() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Deep-link from Research pages: forward category, dates, platform, plus
  // explicit `country=all` / `eventType=all` / `enrichment=all` so the
  // destination shows the same scope without any filter still narrowing.
  const initCategory   = searchParams.get('category')   || 'all';
  const initPlatform   = searchParams.get('platform')   || 'all';
  const initEventType  = searchParams.get('eventType')  || 'all';
  // Default to 'enriched': this study is about advertising content, and ~74% of
  // ad_events are measurement beacons / API telemetry that carry no advertiser data
  // (see docs/00_maintain.md 2026-08-06). Enriched-only hides no gambling row at all
  // and only ~0.5% of classified content. Failed/skipped rows remain one click away,
  // and enrichment health is monitored on /admin/enrichment-monitor.
  const initEnrichment = searchParams.get('enrichment') || 'enriched';
  const initCountry    = searchParams.get('country')    || 'JP';
  const initMemberCode = searchParams.get('memberCode');
  const initDateFrom   = searchParams.get('dateFrom');
  const initDateTo     = searchParams.get('dateTo');
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState<CapturedAd[]>([]);
  const [stats, setStats] = useState<AdStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState(initPlatform);
  const [selectedEventType, setSelectedEventType] = useState(initEventType);
  const [selectedCategory, setSelectedCategory] = useState(initCategory);
  const [selectedEnrichment, setSelectedEnrichment] = useState(initEnrichment);
  const [selectedCountry, setSelectedCountry] = useState(initCountry);
  const [dateFrom, setDateFrom] = useState(() => {
    if (initDateFrom) return new Date(initDateFrom).toISOString();
    const d = new Date(Date.now() - 7 * 86400000);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  });
  const [dateTo, setDateTo] = useState(initDateTo ? new Date(initDateTo).toISOString() : '');
  const [viewMode, setViewMode] = useState<'all' | 'grouped'>('all');
  const [showExcluded, setShowExcluded] = useState(false);
  // 'all' | 'true' (only manually-edited rows) | 'false' (only AI-only rows)
  const [selectedManuallyClassified, setSelectedManuallyClassified] = useState<string>('all');
  const [selectedMembers, setSelectedMembers] = useState<string[]>(initMemberCode ? [initMemberCode] : []);
  const [availableMembers, setAvailableMembers] = useState<any[]>([]);
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState('');
  const [groupedAds, setGroupedAds] = useState<any[]>([]);
  const [selectedAd, setSelectedAd] = useState<CapturedAd | null>(null);
const YOUTUBE_TELEMETRY_RULES = [
  'youtube_pagead_interaction',
  'youtube_ad_viewability',
  // 'youtube_click_redirect', // REMOVED: Clicks are valuable data, not telemetry
  'youtube_ad_conversion',
  'youtube_ad_impression',
  'youtube_log_event',
];

const isDoubleclickRow = (ad: CapturedAd) => ad.platform === 'generic' && ad.ruleName === 'google_doubleclick';
const isYoutubeTelemetryRow = (ad: CapturedAd) =>
  ad.platform === 'youtube' && ad.ruleName !== 'youtube_ad_playback' && YOUTUBE_TELEMETRY_RULES.includes(ad.ruleName);

const [showDoubleclickTelemetry, setShowDoubleclickTelemetry] = useState(false);
const [showYoutubeTelemetry, setShowYoutubeTelemetry] = useState(false);
const [simulateLoadingId, setSimulateLoadingId] = useState<number | null>(null);
const [refreshing, setRefreshing] = useState(false);
const [sortBy, setSortBy] = useState<string>('timestamp');
const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
const memberDropdownRef = useRef<HTMLDivElement>(null);
const [editingCategory, setEditingCategory] = useState(false);
const [newCategory, setNewCategory] = useState('');
const [reclassifyReason, setReclassifyReason] = useState('');
const [reclassifying, setReclassifying] = useState(false);
const [classificationHistory, setClassificationHistory] = useState<any[]>([]);

  const limit = 50;

  useEffect(() => {
    // Load members that have ad data (with counts)
    adminAPI.getCodesWithAdCounts().then((c: any[]) => setAvailableMembers(c)).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [page, selectedPlatform, selectedEventType, selectedCategory, selectedEnrichment, selectedCountry, dateFrom, dateTo, viewMode, showExcluded, selectedMembers, sortBy, sortOrder, selectedManuallyClassified]);

  // Click-outside handler for member dropdown
  useEffect(() => {
    if (!memberSearchOpen) return;
    const handler = (e: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(e.target as Node)) {
        setMemberSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [memberSearchOpen]);

  // Reset reclassification state when selectedAd changes
  useEffect(() => {
    if (selectedAd) {
      setEditingCategory(false);
      setNewCategory(selectedAd.category || '');
      setReclassifyReason('');
      setClassificationHistory([]);
      if (selectedAd.isManuallyClassified) {
        adminAPI.getClassificationHistory(selectedAd.id).then(setClassificationHistory).catch(() => {});
      }
    }
  }, [selectedAd]);

  async function loadData() {
    setRefreshing(true);
    try {
      const authCheck = await authAPI.checkAdminAuth();
      if (!authCheck.authenticated) {
        navigate('/admin');
        return;
      }

      const memberCodesParam = selectedMembers.length > 0 ? selectedMembers.join(',') : undefined;
      const [adsData, statsData] = await Promise.all([
        adminAPI.getCapturedAds(selectedPlatform, limit, page * limit, selectedEventType, selectedCategory, selectedEnrichment, selectedCountry, dateFrom || undefined, dateTo || undefined, viewMode !== 'all' ? viewMode : undefined, showExcluded ? 'true' : undefined, memberCodesParam, sortBy, sortOrder, selectedManuallyClassified !== 'all' ? selectedManuallyClassified : undefined),
        adminAPI.getCapturedAdsStats({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, userCountry: selectedCountry, memberCodes: memberCodesParam }),
      ]);

      if (viewMode === 'grouped') {
        setGroupedAds(adsData.data);
        setAds([]);
      } else {
        setAds(adsData.data);
        setGroupedAds([]);
      }
      setTotal(adsData.total);
      if (statsData) {
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to load captured ads:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    await loadData();
  }

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'timestamp' ? 'desc' : 'asc');
    }
    setPage(0);
  }

  async function handleReclassify() {
    if (!selectedAd || !newCategory) return;
    setReclassifying(true);
    try {
      await adminAPI.reclassifyAd(selectedAd.id, newCategory, reclassifyReason || undefined);
      setSelectedAd({
        ...selectedAd,
        category: newCategory,
        categoryConfidence: 1.0,
        isManuallyClassified: true,
        manualClassifiedBy: 'admin',
        manualClassifiedAt: new Date().toISOString(),
        originalCategory: selectedAd.originalCategory || selectedAd.category,
      });
      setEditingCategory(false);
      loadData();
      adminAPI.getClassificationHistory(selectedAd.id).then(setClassificationHistory).catch(() => {});
    } catch {
      alert(t('capturedAds.saveFailed'));
    } finally {
      setReclassifying(false);
    }
  }

  async function handleBulkReclassifyByAdvertiser() {
    if (!selectedAd || !newCategory) return;
    const domain = selectedAd.advertiserDomain;
    if (!domain) {
      alert('この広告には advertiser_domain が登録されていません。一括変更はできません。');
      return;
    }
    // Detect platform-aggregator domains. For these the actual advertiser is
    // the channel/page/handle (advertiser_name from enrichment), NOT the
    // shared platform domain. We require an enriched name to narrow scope.
    const PLATFORM_DOMAINS = new Set([
      'youtube.com', 'youtu.be', 'm.youtube.com', 'www.youtube.com',
      'facebook.com', 'fb.com', 'm.facebook.com', 'www.facebook.com',
      'instagram.com', 'm.instagram.com', 'www.instagram.com',
      'twitter.com', 'x.com', 't.co', 'www.x.com', 'www.twitter.com',
      'tiktok.com', 'www.tiktok.com',
    ]);
    const isPlatform = PLATFORM_DOMAINS.has(domain.toLowerCase());
    const advertiserName = (selectedAd.enrichedAdvertiserName || selectedAd.advertiserName || '').trim() || null;

    // Friendly platform label so the dialog reads naturally
    const platformLabel = isPlatform
      ? (domain.includes('youtube') || domain.includes('youtu.be') ? 'YouTube'
        : domain.includes('facebook') || domain.includes('fb.com') ? 'Facebook'
        : domain.includes('instagram') ? 'Instagram'
        : domain.includes('tiktok') ? 'TikTok'
        : (domain.includes('twitter') || domain === 'x.com' || domain === 't.co' || domain.includes('www.x.com') || domain.includes('www.twitter.com')) ? 'X / Twitter'
        : domain)
      : domain;

    if (isPlatform && !advertiserName) {
      alert(
        `この広告は「${platformLabel}」プラットフォームの広告です。\n` +
        `「${domain}」はプラットフォーム自体の共通ドメインなので、` +
        `これだけで一括変更すると ${platformLabel} 上のすべての広告に影響してしまいます。\n\n` +
        `この広告には個別の広告主名（チャンネル名・ページ名）が登録されていないため、` +
        `一括変更は実行できません。\n\n` +
        `代わりに「✓ この1件を変更」で個別に修正してください。`
      );
      return;
    }

    // Confirmation message — clearly identify the ACTUAL advertiser scope.
    // For platform ads, the channel/page name is the primary identifier;
    // domain is shown only as parenthetical context.
    const scope = isPlatform
      ? `${platformLabel} チャンネル「${advertiserName}」`
      : `広告主「${domain}」`;
    const ruleNote = isPlatform
      ? `※ プラットフォーム共通ドメインのため、AI判定ルールには登録されません（既存の広告のみ更新）。\n  今後の同じチャンネルの新しい広告は、再度確認・修正が必要になる場合があります。`
      : `OK: 今後同じドメインの広告も自動でこの分類になります（AI判定ルール登録）\nキャンセル: 一括変更のみ（今後の広告はAIが再判定）`;
    const confirmTitle = isPlatform
      ? `${scope} のすべての広告を「${newCategory}」 に一括変更します。\n\n${ruleNote}\n\n実行してよろしいですか？`
      : `${scope} のすべての広告を「${newCategory}」 に一括変更します。\n\n${ruleNote}`;
    const alsoSaveAsOverride = confirm(confirmTitle);

    // For platform domains, the user can't actually opt out of "no rule" since
    // we never create one anyway. The confirm dialog is mostly a sanity check.
    setReclassifying(true);
    try {
      const result = await adminAPI.bulkReclassifyByAdvertiser({
        advertiserDomain: domain,
        advertiserName,
        category: newCategory,
        reason: reclassifyReason || `bulk by advertiser ${domain}${advertiserName ? ` / ${advertiserName}` : ''}`,
        alsoSaveAsOverride,
      });

      // Build a clear human-readable message describing exactly what happened.
      let msg: string;
      const scopeLabel = isPlatform
        ? `「${advertiserName}」（${domain}）`
        : `「${domain}」`;
      if (result.eventsUpdated > 0) {
        msg = `✓ 完了: ${result.eventsUpdated}件の広告を「${newCategory}」に変更しました。\n対象: ${scopeLabel}`;
        if (result.overrideId) {
          msg += `\n今後の同じ広告主の広告も自動的にこの分類になります（AI判定ルール登録済み）。`;
        } else if (result.overrideSkipped) {
          msg += `\n\n⚠ AI判定ルールは登録されませんでした（理由: プラットフォーム共通ドメインのため、ルール化すると他の広告主にも影響するため）。`;
        }
      } else {
        msg = `既存の広告データに ${scopeLabel} に一致するものがありませんでした（0件更新）。`;
        if (result.overrideId) {
          msg += `\n\n✓ ただし、AI判定ルールは保存されたので、今後この広告主の新しい広告は自動的に「${newCategory}」に分類されます。`;
        }
      }
      alert(msg);

      // Mirror the single-reclass behaviour: update the modal state so the
      // category badge reflects the new value immediately, even when 0 list
      // rows were updated (the rule is what matters going forward).
      setSelectedAd({
        ...selectedAd,
        category: newCategory,
        categoryConfidence: 1.0,
        isManuallyClassified: true,
        manualClassifiedBy: 'admin',
        manualClassifiedAt: new Date().toISOString(),
        originalCategory: selectedAd.originalCategory || selectedAd.category,
      });
      setEditingCategory(false);
      loadData();
      adminAPI.getClassificationHistory(selectedAd.id).then(setClassificationHistory).catch(() => {});
    } catch (e: any) {
      alert(e?.message || t('capturedAds.saveFailed'));
    } finally {
      setReclassifying(false);
    }
  }

  function handleExportCSV() {
    const memberCodesParam = selectedMembers.length > 0 ? selectedMembers.join(',') : undefined;
    const url = adminAPI.exportCapturedAdsUrl({
      platform: selectedPlatform,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      category: selectedCategory,
      eventType: selectedEventType,
      enrichmentStatus: selectedEnrichment,
      userCountry: selectedCountry,
      memberCodes: memberCodesParam,
      showExcluded: showExcluded ? 'true' : undefined,
    });
    window.open(url, '_blank');
  }

  function SortHeader({ column, label }: { column: string; label: string }) {
    const isActive = sortBy === column;
    return (
      <th
        className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-kido-100 dark:hover:bg-warm-gray-600 select-none transition-colors"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          <span className="inline-flex flex-col leading-none">
            <ChevronUp className={`w-3 h-3 ${isActive && sortOrder === 'asc' ? 'text-kido-600 dark:text-kido-400' : 'text-gray-300 dark:text-gray-500'}`} />
            <ChevronDown className={`w-3 h-3 -mt-1 ${isActive && sortOrder === 'desc' ? 'text-kido-600 dark:text-kido-400' : 'text-gray-300 dark:text-gray-500'}`} />
          </span>
        </div>
      </th>
    );
  }

  function getPlatformColor(platform: string) {
    const colors: Record<string, string> = {
      youtube: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      facebook: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      instagram: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      x_twitter: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
      generic: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300',
    };
    return colors[platform] || colors.generic;
  }

  function getEventTypeColor(eventType: string) {
    const colors: Record<string, string> = {
      impression: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      click: 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      playback: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      viewability: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
      conversion: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    };
    return colors[eventType] || 'bg-gray-50 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300';
  }

  function getCategoryColor(category: string | null) {
    if (!category) return 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400';

    if (category.startsWith('gambling_')) {
      return 'bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700';
    }

    const colors: Record<string, string> = {
      politics: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      healthcare: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      education: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      economics: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
      tourism: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      culture: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      sports: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300 dark:bg-orange-900/40 dark:text-orange-300',
      other: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
    };
    return colors[category] || 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400';
  }

  function getCategoryLabel(category: string | null) {
    if (!category) return '';
    const key = `categories.${category}`;
    const translated = t(key);
    return translated !== key ? translated : category;
  }

  function getLegalStatus(category: string | null): { status: string; label: string; labelJa: string; color: string; description: string } | null {
    if (!category) return null;

    const statusMap: Record<string, { status: string; label: string; labelJa: string; color: string; description: string }> = {
      gambling_online: {
        status: 'ILLEGAL',
        label: 'ILLEGAL in Japan',
        labelJa: '違法（日本）',
        color: 'bg-red-600 text-white',
        description: 'Online casino advertising has been banned in Japan since September 2025. Operating or advertising online casinos targeting Japanese users is prohibited under Japanese law.',
      },
      gambling_pachinko: {
        status: 'LEGAL',
        label: 'Legal (Regulated)',
        labelJa: '合法（規制対象）',
        color: 'bg-green-600 text-white',
        description: 'Pachinko and pachislot parlors are legal and regulated entertainment facilities in Japan.',
      },
      gambling_public: {
        status: 'LEGAL',
        label: 'Legal (Government)',
        labelJa: '合法（公営）',
        color: 'bg-green-600 text-white',
        description: 'Government-regulated public gambling including horse racing (JRA), boat racing, bicycle racing, auto racing, and lottery (toto/BIG).',
      },
      gambling_game: {
        status: 'LEGAL',
        label: 'Legal (No Real Money)',
        labelJa: '合法（仮想通貨のみ）',
        color: 'bg-green-600 text-white',
        description: 'Casino-style mobile games that use virtual currency only. Legal as long as no real money is exchanged.',
      },
      gambling_gacha_premium: {
        status: 'GRAY',
        label: 'Gray Zone',
        labelJa: 'グレーゾーン',
        color: 'bg-yellow-600 text-white',
        description: 'Premium gacha and online oripa (original pack) services involving real products and money. Legal status unclear — not explicitly prohibited but involves high-value gambling-like mechanics.',
      },
    };
    return statusMap[category] || null;
  }

  function getEnrichmentStatusBadge(status: string | null) {
    if (!status || status === 'pending') return null;

    const badges: Record<string, { label: string; color: string }> = {
      enriched: { label: '✅ エンリッチ済み', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
      failed: { label: '❌ 失敗', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
      skipped: { label: '⏭️ スキップ', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400' },
    };

    return badges[status] || null;
  }

  const formatSlotType = (slot?: string | null) => {
    if (!slot) return '-';
    const map: Record<string, string> = {
      SLOT_TYPE_IN_FEED: 'フィード内',
      SLOT_TYPE_BELOW_PLAYER: 'プレイヤー下部',
      SLOT_TYPE_PLAYER_BYTES: '動画プレイヤー',
    };
    return map[slot] || slot;
  };

  const formatAdEventType = (eventType?: string | null) => {
    if (!eventType) return '-';
    const map: Record<string, string> = {
      ADS_CLIENT_EVENT_TYPE_OPPORTUNITY_RECEIVED: '広告枠検出',
      ADS_CLIENT_EVENT_TYPE_OPPORTUNITY_PROCESSED: '広告処理完了',
      ADS_CLIENT_EVENT_TYPE_LAYOUT_ENTERED: 'レイアウト表示',
      ADS_CLIENT_EVENT_TYPE_LAYOUT_EXITED_NORMALLY: 'レイアウト終了',
      ADS_CLIENT_EVENT_TYPE_SLOT_SCHEDULED: 'スロット確保',
      ADS_CLIENT_EVENT_TYPE_SLOT_EXITED: 'スロット終了',
    };
    return map[eventType] || eventType;
  };

  const totalPages = Math.ceil(total / limit);

  const extractAdUrl = (value?: string | null) => {
    if (!value) return null;
    try {
      const parsed = new URL(value);
      const adurl = parsed.searchParams.get('adurl') || parsed.searchParams.get('ad_url');
      if (!adurl) return null;
      return decodeURIComponent(adurl);
    } catch {
      return null;
    }
  };

  const getSimulateTarget = (ad: CapturedAd) => extractAdUrl(ad.url) || extractAdUrl(ad.advertiserUrl);

  async function handleSimulateClick(ad: CapturedAd) {
    try {
      setSimulateLoadingId(ad.id);
      const result = await adminAPI.simulateCapturedAdClick(ad.id);
      if (result?.finalUrl) {
        window.open(result.finalUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert(t('capturedAds.cannotIdentifyUrl'));
      }
    } catch (error) {
      console.error('Simulate click failed', error);
      alert(t('capturedAds.simulationFailed'));
    } finally {
      setSimulateLoadingId(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Page header (sidebar handles global nav, theme, language, logout) */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('capturedAds.title')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{getVersionString()}</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
            title={t('common.refresh') as string}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? t('common.refreshing') : t('common.refresh')}</span>
          </button>
        </div>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.totalAds')}</div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">{parseInt(stats.totalAds).toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">YouTube</div>
              <div className="text-2xl font-bold text-red-600">{parseInt(stats.youtubeAds).toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Facebook</div>
              <div className="text-2xl font-bold text-blue-600">{parseInt(stats.facebookAds).toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Instagram</div>
              <div className="text-2xl font-bold text-pink-600">{parseInt(stats.instagramAds).toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">X (Twitter)</div>
              <div className="text-2xl font-bold text-sky-600">{parseInt(stats.xTwitterAds || '0').toLocaleString()}</div>
              <div className="text-xs text-amber-600 mt-1 font-semibold">🔬 Research</div>
            </div>

            {/* Enrichment & Classification Stats */}
            <div className="col-span-full border-t border-gray-300 dark:border-warm-gray-600 my-2"></div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg shadow p-4 border border-purple-200 dark:border-purple-700">
              <div className="text-sm text-purple-700 dark:text-purple-300 font-semibold mb-1">{t('capturedAds.enriched')}</div>
              <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                {parseInt(stats.enrichedAds || '0').toLocaleString()}
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                {stats.totalAds && ((parseInt(stats.enrichedAds || '0') / parseInt(stats.totalAds)) * 100).toFixed(1)}%
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 rounded-lg shadow p-4 border border-green-200 dark:border-green-700">
              <div className="text-sm text-green-700 dark:text-green-300 font-semibold mb-1">{t('capturedAds.classified')}</div>
              <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                {parseInt(stats.classifiedAds || '0').toLocaleString()}
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                {stats.totalAds && ((parseInt(stats.classifiedAds || '0') / parseInt(stats.totalAds)) * 100).toFixed(1)}%
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 rounded-lg shadow p-4 border border-red-200 dark:border-red-700">
              <div className="text-sm text-red-700 dark:text-red-300 font-semibold mb-1">🎰 {t('capturedAds.gambling')}</div>
              <div className="text-2xl font-bold text-red-800 dark:text-red-200">
                {parseInt(stats.gamblingAds || '0').toLocaleString()}
              </div>
              <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                {stats.classifiedAds && parseInt(stats.classifiedAds) > 0 && ((parseInt(stats.gamblingAds || '0') / parseInt(stats.classifiedAds)) * 100).toFixed(1)}%
              </div>
            </div>

            {/* Gambling Category Breakdown */}
            <div className="col-span-full border-t border-gray-300 dark:border-warm-gray-600 my-2"></div>
            <div className="col-span-full">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">{t('capturedAds.gamblingBreakdown')}</div>
              <div className="grid grid-cols-5 gap-3">
                {([
                  { key: 'gamblingPachinko', filter: 'gambling_pachinko', emoji: '🎰', catKey: 'gambling_pachinko', legalKey: 'legal', legalColor: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40', borderColor: 'border-green-300 dark:border-green-700', bgColor: 'bg-green-50 dark:bg-green-900/20', hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30' },
                  { key: 'gamblingOnline', filter: 'gambling_online', emoji: '🎲', catKey: 'gambling_online', legalKey: 'illegal', legalColor: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/40', borderColor: 'border-red-400 dark:border-red-700', bgColor: 'bg-red-50 dark:bg-red-900/20', hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900/30' },
                  { key: 'gamblingPublic', filter: 'gambling_public', emoji: '🏇', catKey: 'gambling_public', legalKey: 'legal', legalColor: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40', borderColor: 'border-green-300 dark:border-green-700', bgColor: 'bg-green-50 dark:bg-green-900/20', hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30' },
                  { key: 'gamblingGame', filter: 'gambling_game', emoji: '🎮', catKey: 'gambling_game', legalKey: 'legal', legalColor: 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/40', borderColor: 'border-blue-300 dark:border-blue-700', bgColor: 'bg-blue-50 dark:bg-blue-900/20', hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/30' },
                  { key: 'gamblingGachaPremium', filter: 'gambling_gacha_premium', emoji: '🎴', catKey: 'gambling_gacha_premium', legalKey: 'gray', legalColor: 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/40', borderColor: 'border-amber-300 dark:border-amber-700', bgColor: 'bg-amber-50 dark:bg-amber-900/20', hoverBg: 'hover:bg-amber-100 dark:hover:bg-amber-900/30' },
                ] as const).map(({ key, filter, emoji, catKey, legalKey, legalColor, borderColor, bgColor, hoverBg }) => {
                  const count = parseInt((stats as any)[key] || '0');
                  const isActive = selectedCategory === filter;
                  return (
                    <button key={key} onClick={() => { setSelectedCategory(isActive ? 'all' : filter); setPage(0); }}
                      className={`${isActive ? 'ring-2 ring-kido-500 shadow-md' : ''} ${bgColor} ${hoverBg} rounded-xl border-2 ${borderColor} p-3 text-center cursor-pointer transition-all`}>
                      <div className="text-3xl mb-1">{emoji}</div>
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 leading-tight">{t(`categories.${catKey}`)}</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 my-1">{count.toLocaleString()}</div>
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${legalColor}`}>{t(`legalStatus.${legalKey}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Compact Filters */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3 mb-3">
          {/* Row 1: Dropdown filters */}
          <div className="flex gap-2 flex-wrap items-center mb-2">
            <select value={selectedPlatform} onChange={(e) => { setSelectedPlatform(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200">
              <option value="all">{t('capturedAds.allPlatforms')}</option>
              <option value="youtube">YouTube</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="x_twitter">X (Twitter)</option>
            </select>
            <select value={selectedEventType} onChange={(e) => { setSelectedEventType(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200">
              <option value="all">{t('capturedAds.allEvents')}</option>
              <option value="impression">{t('capturedAds.impression')}</option>
              <option value="click">{t('capturedAds.click')}</option>
              <option value="playback">{t('capturedAds.playback')}</option>
              <option value="viewability">{t('capturedAds.viewability')}</option>
              <option value="conversion">{t('capturedAds.conversion')}</option>
            </select>
            <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200">
              <option value="all">{t('capturedAds.allCategories')}</option>
              <option value="gambling_all">🎰 {t('capturedAds.allGambling')}</option>
              <option value="gambling_illegal">⚠ {t('capturedAds.illegalGambling')} (gambling_online)</option>
              <option value="gambling_legal">✓ {t('capturedAds.legalGambling', 'Legal Gambling')}</option>
              <option value="gambling_gray">◑ {t('capturedAds.grayGambling', 'Gray Zone')}</option>
              <option value="gambling_online">{t('categories.gambling_online')}</option>
              <option value="gambling_pachinko">{t('categories.gambling_pachinko')}</option>
              <option value="gambling_public">{t('categories.gambling_public')}</option>
              <option value="gambling_game">{t('categories.gambling_game')}</option>
              <option value="gambling_gacha_premium">{t('categories.gambling_gacha_premium')}</option>
              <option value="politics">{t('categories.politics')}</option>
              <option value="healthcare">{t('categories.healthcare')}</option>
              <option value="education">{t('categories.education')}</option>
              <option value="economics">{t('categories.economics')}</option>
              <option value="tourism">{t('categories.tourism')}</option>
              <option value="culture">{t('categories.culture')}</option>
              <option value="sports">{t('categories.sports')}</option>
              <option value="youtube">▶️ {t('categories.youtube')}</option>
              <option value="other">{t('categories.other')}</option>
            </select>
            <select value={selectedEnrichment} onChange={(e) => { setSelectedEnrichment(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200">
              <option value="all">{t('capturedAds.allEnrichment')}</option>
              <option value="enriched">{t('capturedAds.enrichedStatus')}</option>
              {/* 'skipped' is ~74% of all rows (tracking beacons / API telemetry with no
                  advertiser content). It was missing from this list, so the dominant
                  status was invisible and the page looked under-enriched. The backend
                  already filters on any enrichment_status value — no API change needed. */}
              <option value="skipped">{t('capturedAds.skippedStatus', '対象外（付加情報なし）')}</option>
              <option value="pending">{t('capturedAds.pendingStatus')}</option>
              <option value="failed">{t('capturedAds.failedStatus')}</option>
            </select>
            {/* Rendered only while the 'skipped' filter is active, so the default
                layout is unchanged for everyone else. */}
            {selectedEnrichment === 'skipped' && (
              <div className="w-full text-xs text-gray-500 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2 mt-1">
                {t('capturedAds.enrichmentHint', '')}
              </div>
            )}
            {/* The list is filtered but the stats header counts every captured event, so
                say plainly why the two differ — otherwise this looks like a discrepancy
                against the report totals. */}
            {selectedEnrichment === 'enriched' && (
              <div className="w-full text-xs text-gray-500 dark:text-gray-400 bg-sky-50 dark:bg-sky-900/20 rounded px-3 py-2 mt-1">
                {t('capturedAds.enrichedHint', '')}
              </div>
            )}
            <select value={selectedManuallyClassified} onChange={(e) => { setSelectedManuallyClassified(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200"
              title={t('capturedAds.manualFilterTitle', 'Filter by manual classification') as string}>
              <option value="all">{t('capturedAds.manualFilterAll', 'All edits')}</option>
              <option value="true">✏️ {t('capturedAds.manualFilterEdited', 'Edited only')}</option>
              <option value="false">{t('capturedAds.manualFilterAiOnly', 'AI-only (not edited)')}</option>
            </select>
            <select value={selectedCountry} onChange={(e) => { setSelectedCountry(e.target.value); setPage(0); }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-sm bg-white focus:border-kido-500 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200">
              <option value="all">🌐 {t('capturedAds.allUsers')}</option>
              <option value="JP">🇯🇵 {t('capturedAds.japan')}</option>
              <option value="VN">🇻🇳 {t('capturedAds.vietnam')}</option>
            </select>
            {/* Member filter */}
            <div className="relative" ref={memberDropdownRef}>
              <button
                onClick={() => setMemberSearchOpen(!memberSearchOpen)}
                className={`px-2 py-1 border rounded text-sm ${selectedMembers.length > 0 ? 'border-kido-500 bg-kido-50 text-kido-700 dark:bg-kido-900 dark:text-kido-300' : 'border-warm-gray-300 dark:border-warm-gray-600 bg-white dark:bg-warm-gray-700 text-gray-700 dark:text-gray-200'}`}
              >
                {t('capturedAds.member')}{selectedMembers.length > 0 ? ` (${selectedMembers.length})` : ''}
              </button>
              {memberSearchOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-warm-gray-800 border border-warm-gray-300 dark:border-warm-gray-600 rounded shadow-lg z-20 w-64 max-h-60 overflow-y-auto">
                  <div className="sticky top-0 bg-white dark:bg-warm-gray-800 p-1 border-b border-warm-gray-200 dark:border-warm-gray-600">
                    <input
                      type="text"
                      value={memberSearchText}
                      onChange={(e) => setMemberSearchText(e.target.value)}
                      placeholder={t('common.search')}
                      className="w-full px-2 py-1 border border-warm-gray-300 dark:border-warm-gray-600 rounded text-sm outline-none focus:border-kido-500 dark:bg-warm-gray-700 dark:text-gray-100"
                      autoFocus
                    />
                  </div>
                  {selectedMembers.length > 0 && (
                    <button onClick={() => { setSelectedMembers([]); setPage(0); }} className="w-full text-left px-2 py-1 text-xs text-red-600 hover:bg-red-50">{t('capturedAds.clearSelection')}</button>
                  )}
                  {availableMembers
                    .filter(m => {
                      if (!memberSearchText) return true;
                      const q = memberSearchText.toLowerCase();
                      return m.code?.toLowerCase().includes(q) || m.memberName?.toLowerCase().includes(q) || m.notes?.toLowerCase().includes(q);
                    })
                    .map((m: any) => (
                      <label key={m.code} className="flex items-center gap-2 px-2 py-1 hover:bg-kido-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(m.code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMembers([...selectedMembers, m.code]);
                            } else {
                              setSelectedMembers(selectedMembers.filter(c => c !== m.code));
                            }
                            setPage(0);
                          }}
                          className="w-3.5 h-3.5 flex-shrink-0"
                        />
                        <span className="truncate flex-1">{m.memberName || m.notes || m.code}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{(m.adCount || 0).toLocaleString()}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setSelectedPlatform('all');
                setSelectedEventType('all');
                setSelectedCategory('all');
                setSelectedEnrichment('all');
                setSelectedCountry('JP');
                setSelectedMembers([]);
                const d = new Date(Date.now() - 7 * 86400000);
                d.setHours(0, 0, 0, 0);
                setDateFrom(d.toISOString());
                setDateTo('');
                setSortBy('timestamp');
                setSortOrder('desc');
                setPage(0);
              }}
              className="text-xs text-gray-500 hover:text-kido-600"
            >
              {t('common.reset')}
            </button>
            <select
              value={`${sortBy}:${sortOrder}`}
              onChange={(e) => {
                const [col, dir] = e.target.value.split(':');
                setSortBy(col);
                setSortOrder(dir as 'asc' | 'desc');
                setPage(0);
              }}
              className="px-2 py-1 border border-warm-gray-300 rounded text-xs bg-white text-gray-700 dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-gray-200"
            >
              <option value="timestamp:desc">{t('capturedAds.sortNewest')}</option>
              <option value="timestamp:asc">{t('capturedAds.sortOldest')}</option>
              <option value="memberCode:asc">{t('capturedAds.sortMemberAsc')}</option>
              <option value="memberCode:desc">{t('capturedAds.sortMemberDesc')}</option>
              <option value="platform:asc">{t('capturedAds.sortPlatform')}</option>
              <option value="advertiserName:asc">{t('capturedAds.sortAdvertiser')}</option>
              <option value="category:asc">{t('capturedAds.sortCategory')}</option>
              <option value="eventType:asc">{t('capturedAds.sortEvent')}</option>
            </select>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-500 text-white rounded text-xs font-semibold transition-colors"
              title="現在のフィルタ条件でCSVをダウンロード"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
          {/* Row 2: Date presets + custom range */}
          <div className="flex gap-1.5 flex-wrap items-center">
            {[
              { label: t('capturedAds.dateToday'), days: 0 },
              { label: t('capturedAds.date1w'), days: 7 },
              { label: t('capturedAds.date1m'), days: 30 },
              { label: t('capturedAds.date3m'), days: 90 },
              { label: t('capturedAds.date1y'), days: 365 },
              { label: t('capturedAds.dateAll'), days: -1 },
            ].map((preset) => {
              const isActive = preset.days === -1
                ? (!dateFrom && !dateTo)
                : preset.days === 0
                  ? (dateFrom && new Date(dateFrom).toDateString() === new Date().toDateString())
                  : (dateFrom && Math.abs(Date.now() - new Date(dateFrom).getTime() - preset.days * 86400000) < 86400000);
              return (
                <button
                  key={preset.label}
                  onClick={() => {
                    if (preset.days === -1) { setDateFrom(''); setDateTo(''); }
                    else if (preset.days === 0) {
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      setDateFrom(today.toISOString()); setDateTo('');
                    } else {
                      const from = new Date(Date.now() - preset.days * 86400000); from.setHours(0, 0, 0, 0);
                      setDateFrom(from.toISOString()); setDateTo('');
                    }
                    setPage(0);
                  }}
                  className={`px-2 py-0.5 rounded text-xs font-semibold transition-colors ${isActive ? 'bg-kido-500 text-white' : 'bg-warm-gray-100 text-gray-600 hover:bg-warm-gray-200 dark:bg-warm-gray-700 dark:text-gray-300 dark:hover:bg-warm-gray-600'}`}
                >{preset.label}</button>
              );
            })}
            <span className="text-gray-400 text-xs">|</span>
            <input type="date" value={dateFrom ? dateFrom.substring(0, 10) : ''} onChange={(e) => { setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : ''); setPage(0); }}
              className="px-1.5 py-0.5 border border-warm-gray-300 dark:border-warm-gray-600 rounded text-xs dark:bg-warm-gray-700 dark:text-gray-200" />
            <span className="text-gray-400 text-xs">〜</span>
            <input type="date" value={dateTo ? dateTo.substring(0, 10) : ''} onChange={(e) => { setDateTo(e.target.value ? new Date(e.target.value + 'T23:59:59').toISOString() : ''); setPage(0); }}
              className="px-1.5 py-0.5 border border-warm-gray-300 dark:border-warm-gray-600 rounded text-xs dark:bg-warm-gray-700 dark:text-gray-200" />
          </div>
        </div>

        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3 mb-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.displayOptions')}</div>
            <p className="text-xs text-gray-500">{t('capturedAds.displayOptionsDesc')}</p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={showDoubleclickTelemetry}
                onChange={(e) => setShowDoubleclickTelemetry(e.target.checked)}
              />
              <span>{t('capturedAds.showDoubleclick')}</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={showYoutubeTelemetry}
                onChange={(e) => setShowYoutubeTelemetry(e.target.checked)}
              />
              <span>{t('capturedAds.showYoutubeTelemetry')}</span>
            </label>
            <div className="border-l border-gray-300 h-5"></div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={viewMode === 'grouped'}
                onChange={(e) => { setViewMode(e.target.checked ? 'grouped' : 'all'); setPage(0); }}
              />
              <span>{t('capturedAds.groupByContent')}</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={showExcluded}
                onChange={(e) => { setShowExcluded(e.target.checked); setPage(0); }}
              />
              <span>{t('capturedAds.showExcluded')}</span>
            </label>
          </div>
        </div>

        {/* Grouped View */}
        {viewMode === 'grouped' && (
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle overflow-hidden mb-3">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-kido-50 dark:bg-warm-gray-700 border-b border-warm-gray-200 dark:border-warm-gray-600">
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.lastDetected')}</th>
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.colPlatform')}</th>
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.content')}</th>
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.colAdvertiser')}</th>
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.category')}</th>
                    <th className="text-right py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.eventCount')}</th>
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.sortEvent')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedAds.map((group: any, idx: number) => (
                    <tr key={group.groupKey || idx} className="border-b border-warm-gray-100 dark:border-warm-gray-700 hover:bg-kido-50 transition-colors">
                      <td className="py-2 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(group.lastSeen).toLocaleString('ja-JP')}
                      </td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getPlatformColor(group.platform)}`}>
                          {group.platform}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-800 max-w-xs truncate" title={group.adTitle || group.contentId || '-'}>
                        {group.enrichedThumbnailUrl && (
                          <img src={group.enrichedThumbnailUrl} alt="" className="w-10 h-10 rounded object-cover inline-block mr-2 align-middle" />
                        )}
                        {group.adTitle || group.enrichedAdTitle || group.contentId || '-'}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600 truncate" title={group.advertiserName || group.advertiserDomain || '-'}>
                        {group.advertiserName || group.enrichedAdvertiserName || group.advertiserDomain || '-'}
                      </td>
                      <td className="py-2 px-4">
                        {group.category && (
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getCategoryColor(group.category)}`}>
                            {getCategoryLabel(group.category)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                          {group.eventCount}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex gap-1 flex-wrap">
                          {(group.eventTypes || []).map((et: string) => (
                            <span key={et} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${getEventTypeColor(et)}`}>
                              {et}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {groupedAds.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-gray-500 dark:text-gray-400">{t('capturedAds.noData')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ads Table */}
        {viewMode === 'all' && (
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle overflow-hidden">
          <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[140px]" /> {/* 日時 */}
                <col className="w-[140px]" /> {/* プラットフォーム */}
                <col className="w-[140px]" /> {/* メンバー */}
                <col className="w-auto" /> {/* 広告タイトル - takes remaining space */}
                <col className="w-[160px]" /> {/* 広告主 */}
                <col className="w-[80px]" /> {/* リンク */}
                <col className="w-[120px]" /> {/* キャンペーン - narrower */}
                <col className="w-[100px]" /> {/* イベント */}
              </colgroup>
              <thead className="bg-kido-50 dark:bg-warm-gray-700 sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  <SortHeader column="timestamp" label={t('capturedAds.colTimestamp')} />
                  <SortHeader column="platform" label={t('capturedAds.colPlatform')} />
                  <SortHeader column="memberCode" label={t('capturedAds.colMember')} />
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.colAdTitle')}</th>
                  <SortHeader column="advertiserName" label={t('capturedAds.colAdvertiser')} />
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.colLink')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300">{t('capturedAds.colCampaign')}</th>
                  <SortHeader column="eventType" label={t('capturedAds.colEvent')} />
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 w-[60px]">{t('capturedAds.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {ads
                  .filter((ad) => {
                    if (!showDoubleclickTelemetry && isDoubleclickRow(ad)) {
                      return false;
                    }
                    if (!showYoutubeTelemetry && isYoutubeTelemetryRow(ad)) {
                      return false;
                    }
                    return true;
                  })
                  .map((ad) => (
                  <tr
                    key={ad.id}
                    onClick={() => setSelectedAd(ad)}
                    className={`border-b border-warm-gray-100 dark:border-warm-gray-700 hover:bg-kido-50 dark:hover:bg-warm-gray-700 cursor-pointer transition-colors ${ad.isExcluded ? 'opacity-40 bg-gray-50' : ''}`}
                  >
                    <td className="py-1.5 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(ad.timestamp).toLocaleString('ja-JP')}
                    </td>
                    <td className="py-1.5 px-3">
                      <div className="flex flex-col gap-1">
                        {/* Platform badge */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getPlatformColor(ad.platform)}`}>
                            {ad.platform}
                          </span>
                          {/* Category badge */}
                          {ad.category && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoryColor(ad.category)}`}>
                              {getCategoryLabel(ad.category)}
                            </span>
                          )}
                          {/* Manual classification badge */}
                          {ad.isManuallyClassified && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                              {t('capturedAds.manual')}
                            </span>
                          )}
                          {/* Legal status badge for gambling */}
                          {ad.category && getLegalStatus(ad.category) && (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getLegalStatus(ad.category)!.color}`}>
                              {getLegalStatus(ad.category)!.labelJa}
                            </span>
                          )}
                          {/* Enrichment status badge */}
                          {getEnrichmentStatusBadge(ad.enrichmentStatus) && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getEnrichmentStatusBadge(ad.enrichmentStatus)!.color}`}>
                              {getEnrichmentStatusBadge(ad.enrichmentStatus)!.label}
                            </span>
                          )}
                          {/* Telemetry badge */}
                          {(isDoubleclickRow(ad) || isYoutubeTelemetryRow(ad)) && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                              {t('capturedAds.telemetry')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-1.5 px-3 text-sm text-gray-700 dark:text-gray-200">
                      <div className="flex items-center gap-2 font-semibold">
                        {ad.devicePlatform === 'android' ? (
                          <Smartphone className="w-4 h-4 text-green-600" />
                        ) : ad.devicePlatform === 'ios' ? (
                          <Apple className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                        ) : ad.ruleName === 'browser_extension' ? (
                          <Chrome className="w-4 h-4 text-blue-600" />
                        ) : null}
                        {ad.userCountryCode === 'VN' ? (
                          <span title="Vietnam" className="text-base leading-none">🇻🇳</span>
                        ) : ad.userCountryCode === 'JP' ? (
                          <span title="Japan" className="text-base leading-none">🇯🇵</span>
                        ) : null}
                        <span>{ad.memberCode || ad.userId}</span>
                      </div>
                      {ad.memberNotes && (
                        <div className="text-xs text-gray-500 truncate max-w-[160px]" title={ad.memberNotes}>
                          {ad.memberNotes}
                        </div>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-sm text-gray-800 dark:text-gray-200 max-w-xs">
                      {/* Prefer enriched data over original, show thumbnail + title */}
                      {(() => {
                        const title = ad.enrichedAdTitle || ad.adTitle;
                        const thumbnail = ad.enrichedThumbnailUrl || ad.metadataImage || ad.meta?.thumbnail_url || ad.meta?.image_url || ad.meta?.video_thumbnail;

                        if (thumbnail) {
                          return (
                            <div className="flex items-center gap-2">
                              <img
                                src={thumbnail.includes('cdninstagram.com') || thumbnail.includes('fbcdn.net')
                                  ? `/api/admin/proxy/instagram-image?url=${encodeURIComponent(thumbnail)}`
                                  : thumbnail}
                                alt="Ad preview"
                                className="w-12 h-12 rounded object-cover border border-gray-200 flex-shrink-0"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <span className="truncate" title={title || undefined}>
                                {title || ad.meta?.caption_preview || ad.meta?.tweet_text || ad.contentId || '-'}
                              </span>
                            </div>
                          );
                        }

                        return <span className="truncate" title={title || undefined}>{title || ad.contentId || '-'}</span>;
                      })()}
                    </td>
                    <td className="py-1.5 px-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {ad.enrichedAdvertiserName || ad.advertiserName || ad.advertiserDomain || ad.adDescription || '-'}
                        </span>
                        {ad.enrichedAdvertiserCategory && (
                          <span className="text-xs text-gray-500">
                            {ad.enrichedAdvertiserCategory}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 px-3 text-sm text-blue-600">
                      {ad.landingUrl ? (
                        <a
                          href={ad.landingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t('capturedAds.openLink')}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-sm text-gray-600 truncate" title={ad.campaign || '-'}>
                      {ad.campaign || '-'}
                    </td>
                    <td className="py-1.5 px-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getEventTypeColor(ad.eventType)}`}>
                        {ad.eventType}
                      </span>
                    </td>
                    <td className="py-1.5 px-3" onClick={(e) => e.stopPropagation()}>
                      {ad.isExcluded ? (
                        <button
                          onClick={async () => {
                            await adminAPI.restoreAd(ad.id);
                            loadData();
                          }}
                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900/70"
                          title={`${t('capturedAds.excludeReasonLabel')} ${ad.exclusionReason || '-'}`}
                        >{t('capturedAds.restore')}</button>
                      ) : (
                        <button
                          onClick={async () => {
                            const reason = prompt(t('capturedAds.excludeReason'));
                            if (reason === null) return;
                            await adminAPI.excludeAd(ad.id, reason || undefined);
                            loadData();
                          }}
                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900/70"
                        >{t('capturedAds.exclude')}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 dark:bg-warm-gray-700/50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {t('common.showing', { total: total.toLocaleString(), from: page * limit + 1, to: Math.min((page + 1) * limit, total) })}
            </div>
            <div className="flex gap-1 items-center">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 bg-white dark:bg-warm-gray-700 border border-gray-300 dark:border-warm-gray-600 rounded hover:bg-gray-50 dark:hover:bg-warm-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm dark:text-gray-200"
              >
                {t('capturedAds.previous')}
              </button>

              {/* Page numbers */}
              {(() => {
                const pages: (number | string)[] = [];
                const currentPage = page + 1;
                const maxPages = totalPages;

                // Always show first page
                pages.push(1);

                // Calculate range around current page
                let startPage = Math.max(2, currentPage - 1);
                let endPage = Math.min(maxPages - 1, currentPage + 1);

                // Add ellipsis after first page if needed
                if (startPage > 2) {
                  pages.push('...');
                }

                // Add pages around current page
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }

                // Add ellipsis before last page if needed
                if (endPage < maxPages - 1) {
                  pages.push('...');
                }

                // Always show last page if there's more than one page
                if (maxPages > 1) {
                  pages.push(maxPages);
                }

                return pages.map((pageNum, idx) => {
                  if (pageNum === '...') {
                    return (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }

                  const isCurrentPage = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage((pageNum as number) - 1)}
                      className={`px-3 py-1 rounded text-sm ${
                        isCurrentPage
                          ? 'bg-blue-600 text-white font-semibold'
                          : 'bg-white dark:bg-warm-gray-700 border border-gray-300 dark:border-warm-gray-600 hover:bg-gray-50 dark:hover:bg-warm-gray-600 text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}

              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 bg-white dark:bg-warm-gray-700 border border-gray-300 dark:border-warm-gray-600 rounded hover:bg-gray-50 dark:hover:bg-warm-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm dark:text-gray-200"
              >
                {t('capturedAds.next')}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedAd && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-warm-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-warm-gray-800 border-b border-gray-200 dark:border-warm-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('capturedAds.adDetail')}</h3>
              <button
                onClick={() => setSelectedAd(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Platform & Event */}
              <div className="flex gap-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.platformLabel')}</div>
                  <span className={`px-3 py-1 rounded font-semibold ${getPlatformColor(selectedAd.platform)}`}>
                    {selectedAd.platform}
                  </span>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.eventTypeLabel')}</div>
                  <span className={`px-3 py-1 rounded font-semibold ${getEventTypeColor(selectedAd.eventType)}`}>
                    {selectedAd.eventType}
                  </span>
                </div>
              </div>

              {/* Member */}
              <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded-lg p-4 flex flex-col gap-1">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.memberInfo')}</div>
                <div className="flex items-center gap-2 text-gray-800 font-semibold">
                  {selectedAd.devicePlatform === 'android' ? (
                    <Smartphone className="w-5 h-5 text-green-600" />
                  ) : selectedAd.devicePlatform === 'ios' ? (
                    <Apple className="w-5 h-5 text-gray-700" />
                  ) : selectedAd.ruleName === 'browser_extension' ? (
                    <Chrome className="w-5 h-5 text-blue-600" />
                  ) : null}
                  {selectedAd.userCountryCode === 'VN' ? (
                    <span title="Vietnam" className="text-lg leading-none">🇻🇳</span>
                  ) : selectedAd.userCountryCode === 'JP' ? (
                    <span title="Japan" className="text-lg leading-none">🇯🇵</span>
                  ) : null}
                  <span>{selectedAd.memberCode || selectedAd.userId}</span>
                </div>
                {selectedAd.memberNotes && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">{t('capturedAds.notes')}: {selectedAd.memberNotes}</div>
                )}
                <div className="text-sm text-gray-500">VPN IP: {selectedAd.userId}</div>
              </div>

              {(isDoubleclickRow(selectedAd) || isYoutubeTelemetryRow(selectedAd)) && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 text-sm text-yellow-800 dark:text-yellow-200">
                  {t('capturedAds.telemetryNotice')}
                  {selectedAd.relatedAd
                    ? ` ${t('capturedAds.telemetryCreative')}`
                    : ` ${t('capturedAds.telemetryPlayback')}`}
                </div>
              )}

              {selectedAd.relatedAd && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex gap-4 items-center">
                  {selectedAd.relatedAd.metadataImage && (
                    <img
                      src={selectedAd.relatedAd.metadataImage}
                      alt=""
                      className="w-32 h-20 object-cover rounded border border-blue-100 dark:border-blue-800 bg-white dark:bg-warm-gray-700"
                    />
                  )}
                  <div>
                    <div className="text-sm text-blue-700 dark:text-blue-300 font-semibold mb-1">{t('capturedAds.relatedCreative')}</div>
                    <div className="text-gray-800 dark:text-gray-100 font-semibold">
                      {selectedAd.relatedAd.adTitle || t('capturedAds.noTitle')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedAd.relatedAd.advertiserName || selectedAd.relatedAd.advertiserDomain || t('capturedAds.noAdvertiser')}
                    </div>
                    {selectedAd.relatedAd.advertiserUrl && (
                      <a
                        href={selectedAd.relatedAd.advertiserUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 inline-flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('capturedAds.advertiserPage')}
                      </a>
                    )}
                    {(selectedAd.relatedAd.creativeUrl || selectedAd.relatedAd.contentId) && (
                      <a
                        href={
                          selectedAd.relatedAd.creativeUrl ||
                          `https://www.youtube.com/watch?v=${selectedAd.relatedAd.contentId}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-600 inline-flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {t('capturedAds.openCreativeVideo')}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {selectedAd.platform === 'youtube' && selectedAd.eventType === 'impression' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-blue-700 dark:text-blue-300 font-semibold">{t('capturedAds.youtubeImpressionDetail')}</div>
                    {(selectedAd.creativeUrl ||
                      selectedAd.relatedAd?.creativeUrl ||
                      selectedAd.contentId ||
                      selectedAd.relatedAd?.contentId) && (
                      <a
                        href={
                          selectedAd.creativeUrl ||
                          selectedAd.relatedAd?.creativeUrl ||
                          (selectedAd.contentId
                            ? `https://www.youtube.com/watch?v=${selectedAd.contentId}`
                            : selectedAd.relatedAd?.contentId
                            ? `https://www.youtube.com/watch?v=${selectedAd.relatedAd.contentId}`
                            : '#')
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 inline-flex items-center gap-1"
                      >
                        {t('capturedAds.openCreative')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                    {selectedAd.slotType && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.slotPosition')}</div>
                        <div className="font-medium">{formatSlotType(selectedAd.slotType)}</div>
                      </div>
                    )}
                    {selectedAd.adEventType && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.event')}</div>
                        <div className="font-medium">{formatAdEventType(selectedAd.adEventType)}</div>
                      </div>
                    )}
                    {selectedAd.watchTimeMs && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.watchTime')}</div>
                        <div className="font-medium">{(selectedAd.watchTimeMs / 1000).toFixed(1)} {t('capturedAds.seconds')}</div>
                      </div>
                    )}
                    {selectedAd.adVideoCpn && (
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.adCpn')}</div>
                        <div className="font-mono text-xs break-all dark:text-gray-300">{selectedAd.adVideoCpn}</div>
                      </div>
                    )}
                    {selectedAd.contentCpn && (
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.contentCpn')}</div>
                        <div className="font-mono text-xs break-all dark:text-gray-300">{selectedAd.contentCpn}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm text-gray-600 dark:text-gray-400">{t('capturedAds.landingUrl')}</div>
                  {getSimulateTarget(selectedAd) && (
                    <button
                      onClick={() => handleSimulateClick(selectedAd)}
                      disabled={simulateLoadingId === selectedAd.id}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      {simulateLoadingId === selectedAd.id ? t('capturedAds.fetchingTarget') : t('capturedAds.getTarget')}
                    </button>
                  )}
                </div>
                {selectedAd.landingUrl ? (
                  <a
                    href={selectedAd.landingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 break-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {selectedAd.landingUrl}
                  </a>
                ) : (
                  <div className="text-sm text-gray-500">{t('capturedAds.noUrl')}</div>
                )}
              </div>

              {/* Ad Title */}
              {selectedAd.adTitle && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.adTitle')}</div>
                  <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">{selectedAd.adTitle}</div>
                </div>
              )}

              {/* Ad Description */}
              {selectedAd.adDescription && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.adDescription')}</div>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">{selectedAd.adDescription}</div>
                </div>
              )}

              {/* Advertiser */}
              {selectedAd.advertiserDomain && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('capturedAds.advertiserDomain')}</div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-blue-600">{selectedAd.advertiserDomain}</span>
                    {selectedAd.advertiserUrl && (
                      <a
                        href={selectedAd.advertiserUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Instagram Specific Content */}
              {selectedAd.platform === 'instagram' && selectedAd.meta && (
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg p-4 space-y-3 border border-pink-200 dark:border-pink-800">
                  <div className="flex items-center gap-2 text-pink-700 dark:text-pink-300 font-semibold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram Sponsored Post
                  </div>

                  {/* Instagram Advertiser */}
                  {selectedAd.meta.advertiser_name && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.colAdvertiser')}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-gray-900 dark:text-gray-100 font-semibold">{selectedAd.meta.advertiser_name}</div>
                        {selectedAd.meta.advertiser_username && (
                          <a
                            href={`https://instagram.com/${selectedAd.meta.advertiser_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-600 hover:text-pink-700 text-sm"
                          >
                            @{selectedAd.meta.advertiser_username}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Instagram Caption */}
                  {(selectedAd.meta.caption_full || selectedAd.meta.caption_preview) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.postText')}</div>
                      <div className="bg-white dark:bg-warm-gray-700 rounded p-3 text-sm border border-gray-200 dark:border-warm-gray-600 whitespace-pre-wrap">
                        {selectedAd.meta.caption_full || selectedAd.meta.caption_preview}
                      </div>
                    </div>
                  )}

                  {/* Instagram Image */}
                  {(selectedAd.meta.image_url || selectedAd.meta.thumbnail_url) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.postImage')}</div>
                      <img
                        src={`/api/admin/proxy/instagram-image?url=${encodeURIComponent(selectedAd.meta.thumbnail_url || selectedAd.meta.image_url)}`}
                        alt="Instagram ad"
                        className="max-h-96 rounded border border-gray-200 object-contain bg-white w-full"
                        onError={(e) => {
                          // Fallback to high-res if thumbnail fails
                          if (e.currentTarget.src.includes(encodeURIComponent(selectedAd.meta.thumbnail_url)) && selectedAd.meta.image_url) {
                            e.currentTarget.src = `/api/admin/proxy/instagram-image?url=${encodeURIComponent(selectedAd.meta.image_url)}`;
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Instagram Post Link */}
                  {selectedAd.meta.instagram_url && (
                    <div>
                      <a
                        href={selectedAd.meta.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-pink-600 hover:text-pink-700 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View on Instagram
                      </a>
                    </div>
                  )}

                  {/* Engagement Stats */}
                  {selectedAd.meta.like_count !== undefined && (
                    <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.likes')}:</span>
                        <span className="ml-1 font-semibold">{selectedAd.meta.like_count.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* YouTube Specific Content */}
              {selectedAd.platform === 'youtube' && selectedAd.enrichmentStatus === 'enriched' && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-lg p-4 space-y-3 border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0.012 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    YouTube Video Ad
                  </div>

                  {/* Video Thumbnail */}
                  {(selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Thumbnail</div>
                      <img
                        src={selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage || undefined}
                        alt="YouTube ad thumbnail"
                        className="w-full rounded border border-gray-200 bg-white"
                      />
                    </div>
                  )}

                  {/* Advertiser/Channel Info */}
                  {(selectedAd.enrichedAdvertiserName || selectedAd.advertiserName) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Channel</div>
                      <div className="flex items-center gap-2">
                        <div className="text-gray-900 dark:text-gray-100 font-semibold">
                          {selectedAd.enrichedAdvertiserName || selectedAd.advertiserName}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Video Title */}
                  {(selectedAd.enrichedAdTitle || selectedAd.adTitle) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.adTitle')}</div>
                      <div className="bg-white dark:bg-warm-gray-700 rounded p-3 text-sm border border-gray-200 dark:border-warm-gray-600">
                        {selectedAd.enrichedAdTitle || selectedAd.adTitle}
                      </div>
                    </div>
                  )}

                  {/* Video Description */}
                  {(selectedAd.enrichedAdDescription || selectedAd.adDescription) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.adDescription')}</div>
                      <div className="bg-white dark:bg-warm-gray-700 rounded p-3 text-sm border border-gray-200 dark:border-warm-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {selectedAd.enrichedAdDescription || selectedAd.adDescription}
                      </div>
                    </div>
                  )}

                  {/* Video Stats (from platformMetadata) */}
                  {selectedAd.platformMetadata && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedAd.platformMetadata.view_count && (
                        <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.viewCount')}</div>
                          <div className="font-semibold dark:text-gray-100">{parseInt(selectedAd.platformMetadata.view_count).toLocaleString()}</div>
                        </div>
                      )}
                      {selectedAd.platformMetadata.like_count && (
                        <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.likes')}</div>
                          <div className="font-semibold">{parseInt(selectedAd.platformMetadata.like_count).toLocaleString()}</div>
                        </div>
                      )}
                      {selectedAd.platformMetadata.duration_seconds && (
                        <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.duration')}</div>
                          <div className="font-semibold">{Math.floor(selectedAd.platformMetadata.duration_seconds / 60)}:{String(selectedAd.platformMetadata.duration_seconds % 60).padStart(2, '0')}</div>
                        </div>
                      )}
                      {selectedAd.platformMetadata.published_at && (
                        <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.publishedDate')}</div>
                          <div className="font-semibold text-xs">{new Date(selectedAd.platformMetadata.published_at).toLocaleDateString('ja-JP')}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Link */}
                  {(selectedAd.contentId || selectedAd.meta?.ad_video_id) && (
                    <div>
                      <a
                        href={`https://www.youtube.com/watch?v=${selectedAd.contentId || selectedAd.meta?.ad_video_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('capturedAds.openOnYoutube')}
                      </a>
                    </div>
                  )}

                  {/* Enrichment Info */}
                  {selectedAd.enrichmentSource && (
                    <div className="text-xs text-gray-500 border-t pt-2">
                      {t('capturedAds.dataSource')}: {selectedAd.enrichmentSource === 'youtube_api' ? 'YouTube Data API' : selectedAd.enrichmentSource}
                      {selectedAd.enrichmentConfidence && ` (${t('capturedAds.confidence')}: ${selectedAd.enrichmentConfidence})`}
                    </div>
                  )}
                </div>
              )}

              {/* Facebook Specific Content */}
              {selectedAd.platform === 'facebook' && selectedAd.enrichmentStatus === 'enriched' && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 space-y-3 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-semibold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook Sponsored Post
                  </div>

                  {/* Post Image/Video */}
                  {(selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage || selectedAd.meta?.image_url) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.postImage')}</div>
                      <img
                        src={(() => {
                          const img = selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage || selectedAd.meta?.image_url;
                          return img && (img.includes('cdninstagram.com') || img.includes('fbcdn.net'))
                            ? `/api/admin/proxy/instagram-image?url=${encodeURIComponent(img)}`
                            : img;
                        })()}
                        alt="Facebook post"
                        className="w-full rounded border border-gray-200 bg-white"
                      />
                    </div>
                  )}

                  {/* Page/Advertiser Info */}
                  {(selectedAd.enrichedAdvertiserName || selectedAd.advertiserDomain) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.pageInfo')}</div>
                      <div className="flex items-center gap-2">
                        {selectedAd.platformMetadata?.page_profile_picture_url && (
                          <img
                            src={selectedAd.platformMetadata.page_profile_picture_url.includes('fbcdn.net')
                              ? `/api/admin/proxy/instagram-image?url=${encodeURIComponent(selectedAd.platformMetadata.page_profile_picture_url)}`
                              : selectedAd.platformMetadata.page_profile_picture_url}
                            alt="Page"
                            className="w-10 h-10 rounded-full border border-gray-200"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100">
                            {selectedAd.enrichedAdvertiserName || selectedAd.advertiserDomain}
                          </div>
                          {selectedAd.enrichedAdvertiserCategory && (
                            <div className="text-xs text-gray-500">
                              {selectedAd.enrichedAdvertiserCategory}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Post Text */}
                  {(selectedAd.enrichedAdDescription || selectedAd.meta?.post_text) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capturedAds.postText')}</div>
                      <div className="text-sm text-gray-800 bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                        {selectedAd.enrichedAdDescription || selectedAd.meta?.post_text}
                      </div>
                    </div>
                  )}

                  {/* Page Stats */}
                  {selectedAd.platformMetadata && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('capturedAds.pageInfo')}</div>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedAd.platformMetadata.fan_count && (
                          <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.fanCount')}</div>
                            <div className="font-semibold text-blue-700 dark:text-blue-300">
                              {parseInt(selectedAd.platformMetadata.fan_count).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {selectedAd.platformMetadata.category && (
                          <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.pageCategory')}</div>
                            <div className="font-semibold text-gray-700 dark:text-gray-200 text-sm">
                              {selectedAd.platformMetadata.category}
                            </div>
                          </div>
                        )}
                        {selectedAd.platformMetadata.website && (
                          <div className="bg-white dark:bg-warm-gray-700 rounded p-2 border border-gray-200 dark:border-warm-gray-600 col-span-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('capturedAds.website')}</div>
                            <a
                              href={selectedAd.platformMetadata.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline truncate block"
                            >
                              {selectedAd.platformMetadata.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Link to Facebook */}
                  {(selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl) && (
                    <div>
                      <a
                        href={selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('capturedAds.openOnFacebook')}
                      </a>
                    </div>
                  )}

                  {/* Enrichment Info */}
                  {selectedAd.enrichmentSource && (
                    <div className="text-xs text-gray-500 border-t pt-2">
                      {t('capturedAds.dataSource')}: {selectedAd.enrichmentSource === 'facebook_api' ? 'Facebook Graph API' : selectedAd.enrichmentSource}
                      {selectedAd.enrichmentConfidence && ` (${t('capturedAds.confidence')}: ${selectedAd.enrichmentConfidence})`}
                    </div>
                  )}
                </div>
              )}

              {/* X/Twitter Specific Content */}
              {selectedAd.platform === 'x' && selectedAd.enrichmentStatus === 'enriched' && (
                <div className="bg-gradient-to-r from-gray-800 to-black rounded-lg p-4 space-y-3 border border-gray-600">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X (Twitter) Promoted Post
                  </div>

                  {/* Tweet Image/Video */}
                  {(selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage || selectedAd.meta?.image_url || selectedAd.meta?.media_url) && (
                    <div>
                      <div className="text-xs text-gray-300 mb-1">ツイート画像 / Tweet Image</div>
                      <img
                        src={selectedAd.enrichedThumbnailUrl || selectedAd.metadataImage || selectedAd.meta?.image_url || selectedAd.meta?.media_url}
                        alt="Tweet media"
                        className="w-full rounded border border-gray-600 bg-gray-900"
                      />
                    </div>
                  )}

                  {/* User Info */}
                  {(selectedAd.enrichedAdvertiserName || selectedAd.advertiserDomain) && (
                    <div>
                      <div className="text-xs text-gray-300 mb-1">アカウント / Account</div>
                      <div className="flex items-center gap-2">
                        {selectedAd.platformMetadata?.profile_image_url && (
                          <img
                            src={selectedAd.platformMetadata.profile_image_url}
                            alt="Profile"
                            className="w-10 h-10 rounded-full border border-gray-600"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-white">
                            {selectedAd.enrichedAdvertiserName || selectedAd.advertiserDomain}
                          </div>
                          {selectedAd.platformMetadata?.username && (
                            <div className="text-xs text-gray-400">
                              @{selectedAd.platformMetadata.username}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tweet Text */}
                  {(selectedAd.enrichedAdDescription || selectedAd.meta?.tweet_text) && (
                    <div>
                      <div className="text-xs text-gray-300 mb-1">ツイートテキスト / Tweet Text</div>
                      <div className="text-sm text-white bg-gray-900 rounded p-2 border border-gray-600">
                        {selectedAd.enrichedAdDescription || selectedAd.meta?.tweet_text}
                      </div>
                    </div>
                  )}

                  {/* Tweet Stats */}
                  {selectedAd.platformMetadata && (
                    <div>
                      <div className="text-xs text-gray-300 mb-2">エンゲージメント / Engagement</div>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedAd.platformMetadata.like_count !== undefined && (
                          <div className="bg-gray-900 rounded p-2 border border-gray-600">
                            <div className="text-xs text-gray-400">いいね</div>
                            <div className="font-semibold text-red-400">
                              {parseInt(selectedAd.platformMetadata.like_count).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {selectedAd.platformMetadata.retweet_count !== undefined && (
                          <div className="bg-gray-900 rounded p-2 border border-gray-600">
                            <div className="text-xs text-gray-400">RT</div>
                            <div className="font-semibold text-green-400">
                              {parseInt(selectedAd.platformMetadata.retweet_count).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {selectedAd.platformMetadata.reply_count !== undefined && (
                          <div className="bg-gray-900 rounded p-2 border border-gray-600">
                            <div className="text-xs text-gray-400">返信</div>
                            <div className="font-semibold text-blue-400">
                              {parseInt(selectedAd.platformMetadata.reply_count).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Link to Twitter */}
                  {(selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || selectedAd.platformMetadata?.tweet_url) && (
                    <div>
                      <a
                        href={selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || selectedAd.platformMetadata?.tweet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Xで開く
                      </a>
                    </div>
                  )}

                  {/* Enrichment Info */}
                  {selectedAd.enrichmentSource && (
                    <div className="text-xs text-gray-400 border-t border-gray-600 pt-2">
                      データソース: {selectedAd.enrichmentSource === 'twitter_api' ? 'X (Twitter) API' : selectedAd.enrichmentSource}
                      {selectedAd.enrichmentConfidence && ` (信頼度: ${selectedAd.enrichmentConfidence})`}
                    </div>
                  )}
                </div>
              )}

              {/* Landing Metadata */}
              {(selectedAd.metadataStatus ||
                selectedAd.metadataSiteName ||
                selectedAd.metadataDescription ||
                selectedAd.metadataImage) && (
                <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">ランディングページ情報</div>
                    {selectedAd.metadataStatus && (
                      <span className="text-xs font-semibold text-gray-600 px-2 py-1 bg-white rounded border border-gray-200">
                        {selectedAd.metadataStatus}
                      </span>
                    )}
                  </div>
                  {selectedAd.metadataSiteName && (
                    <div className="text-gray-800 font-semibold">{selectedAd.metadataSiteName}</div>
                  )}
                  {selectedAd.metadataDescription && (
                    <div className="text-sm text-gray-700">{selectedAd.metadataDescription}</div>
                  )}
                  {selectedAd.metadataImage && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">サムネイル</div>
                      <img
                        src={
                          selectedAd.metadataImage.includes('cdninstagram.com') || selectedAd.metadataImage.includes('fbcdn.net')
                            ? `/api/admin/proxy/instagram-image?url=${encodeURIComponent(selectedAd.metadataImage)}`
                            : selectedAd.metadataImage
                        }
                        alt="Landing thumbnail"
                        className="max-h-48 rounded border border-gray-200 object-contain bg-white"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Campaign Info */}
              {(selectedAd.campaign || selectedAd.source || selectedAd.medium) && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">キャンペーン情報</div>
                  <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded p-3 space-y-1">
                    {selectedAd.campaign && (
                      <div className="flex gap-2">
                        <span className="text-gray-600 w-20">Campaign:</span>
                        <span className="font-mono text-sm">{selectedAd.campaign}</span>
                      </div>
                    )}
                    {selectedAd.source && (
                      <div className="flex gap-2">
                        <span className="text-gray-600 w-20">Source:</span>
                        <span className="font-mono text-sm">{selectedAd.source}</span>
                      </div>
                    )}
                    {selectedAd.medium && (
                      <div className="flex gap-2">
                        <span className="text-gray-600 w-20">Medium:</span>
                        <span className="font-mono text-sm">{selectedAd.medium}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Technical Details */}
              <div>
                <div className="text-sm text-gray-600 mb-2">技術情報</div>
                <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded p-3 space-y-1 text-sm">
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">AD ID:</span>
                    <span className="font-mono">{selectedAd.adId || '-'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">Content ID:</span>
                    <span className="font-mono">{selectedAd.contentId || '-'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">User ID:</span>
                    <span className="font-mono">{selectedAd.userId}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">Rule:</span>
                    <span className="font-mono">{selectedAd.ruleName}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">Confidence:</span>
                    <span className="font-semibold">{selectedAd.confidence}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-32">Timestamp:</span>
                    <span>{new Date(selectedAd.timestamp).toLocaleString('ja-JP')}</span>
                  </div>
                </div>
              </div>

              {/* Ad Content (from browser extension) */}
              {selectedAd.meta?.ad_content && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">広告コンテンツ</div>
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 space-y-3 border border-green-200">
                    {selectedAd.meta.ad_content.overlay_text && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">広告テキスト</div>
                        <div className="bg-white rounded p-2 text-sm border border-gray-200">
                          {selectedAd.meta.ad_content.overlay_text}
                        </div>
                      </div>
                    )}

                    {selectedAd.meta.ad_content.cta_buttons && selectedAd.meta.ad_content.cta_buttons.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">ボタン・CTA</div>
                        <div className="flex flex-wrap gap-2">
                          {selectedAd.meta.ad_content.cta_buttons.map((btn: string, idx: number) => (
                            <span key={idx} className="bg-white px-3 py-1 rounded border border-blue-200 text-sm">
                              {btn}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedAd.meta.ad_content.ad_images && selectedAd.meta.ad_content.ad_images.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">広告画像</div>
                        <div className="flex flex-wrap gap-2">
                          {selectedAd.meta.ad_content.ad_images.map((img: string, idx: number) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Ad image ${idx + 1}`}
                              className="h-20 rounded border border-gray-200 bg-white object-contain"
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedAd.meta.ad_content.ad_area_dimensions && (
                      <div className="text-xs text-gray-500">
                        広告サイズ: {selectedAd.meta.ad_content.ad_area_dimensions.width} × {selectedAd.meta.ad_content.ad_area_dimensions.height}px
                      </div>
                    )}

                    {selectedAd.meta.ad_content.ad_links && selectedAd.meta.ad_content.ad_links.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">広告リンク</div>
                        <div className="space-y-1">
                          {selectedAd.meta.ad_content.ad_links.map((link: any, idx: number) => (
                            <div key={idx} className="bg-white rounded p-2 text-xs border border-gray-200">
                              <div className="font-semibold text-gray-700 dark:text-gray-300">{link.text}</div>
                              <div className="text-gray-500 break-all">{link.url}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Classification Summary & Evidence */}
              {(selectedAd.category || selectedAd.enrichmentStatus) && (
                <div className={`rounded-lg p-4 space-y-3 border ${
                  selectedAd.category === 'gambling_online'
                    ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-300'
                    : selectedAd.category?.startsWith('gambling_')
                    ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300'
                    : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                }`}>
                  <div className={`flex items-center gap-2 font-semibold ${
                    selectedAd.category === 'gambling_online' ? 'text-red-700' : 'text-purple-700'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    AI分類・エビデンス / Classification & Evidence
                  </div>

                  {/* Legal Status Banner (for gambling categories) */}
                  {(() => {
                    const legal = getLegalStatus(selectedAd.category);
                    if (!legal) return null;
                    return (
                      <div className={`rounded-lg p-3 ${
                        legal.status === 'ILLEGAL' ? 'bg-red-100 border-2 border-red-400' :
                        legal.status === 'GRAY' ? 'bg-yellow-100 border-2 border-yellow-400' :
                        'bg-green-100 border-2 border-green-400'
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${legal.color}`}>
                            {legal.labelJa}
                          </span>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{legal.label}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-2">{legal.description}</div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-3">
                    {/* Category with edit */}
                    <div className="bg-white dark:bg-warm-gray-700 rounded p-3 border border-purple-100 dark:border-warm-gray-600">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-500 font-semibold">カテゴリー / Category</div>
                        <button
                          onClick={() => { setEditingCategory(!editingCategory); setNewCategory(''); setReclassifyReason(''); }}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold transition-colors ${
                            editingCategory
                              ? 'bg-gray-200 dark:bg-warm-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300'
                              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                          }`}
                          title={editingCategory ? 'キャンセル' : 'この分類を編集してAIに学習させる'}
                        >
                          {editingCategory
                            ? <>✕ キャンセル</>
                            : <>✏️ 分類を編集</>}
                        </button>
                      </div>

                      {!editingCategory ? (
                        <>
                          {selectedAd.category ? (
                            <div className={`inline-flex items-center px-3 py-1 rounded font-semibold text-sm ${getCategoryColor(selectedAd.category)}`}>
                              {getCategoryLabel(selectedAd.category)}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">未分類</div>
                          )}
                          {selectedAd.isManuallyClassified && (
                            <div className="text-xs text-orange-600 mt-1 font-semibold">
                              手動修正済み ({selectedAd.manualClassifiedBy})
                            </div>
                          )}
                          {selectedAd.originalCategory && selectedAd.originalCategory !== selectedAd.category && (
                            <div className="text-xs text-gray-400 mt-1">
                              元の分類: {getCategoryLabel(selectedAd.originalCategory)}
                            </div>
                          )}
                          {selectedAd.categoryConfidence != null && (
                            <div className="text-xs text-gray-500 mt-1">
                              信頼度: {(Number(selectedAd.categoryConfidence) * 100).toFixed(1)}%
                            </div>
                          )}
                          {selectedAd.classifiedAt && (
                            <div className="text-xs text-gray-400 mt-1">
                              分類日時: {new Date(selectedAd.classifiedAt).toLocaleString('ja-JP')}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="space-y-2 mt-1">
                          <select
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-warm-gray-600 rounded text-sm dark:bg-warm-gray-700 dark:text-gray-100"
                          >
                            <option value="">-- 選択 --</option>
                            <optgroup label="ギャンブル">
                              <option value="gambling_pachinko">🎰 パチンコ・スロット</option>
                              <option value="gambling_online">🎲 オンラインカジノ</option>
                              <option value="gambling_public">🏇 公営ギャンブル</option>
                              <option value="gambling_game">🎮 ガチャゲーム</option>
                              <option value="gambling_gacha_premium">🎴 プレミアムガチャ</option>
                            </optgroup>
                            <optgroup label="一般カテゴリー">
                              <option value="politics">🏛️ 政治</option>
                              <option value="healthcare">🏥 医療・健康</option>
                              <option value="education">📚 教育</option>
                              <option value="economics">💰 経済・ショッピング</option>
                              <option value="tourism">✈️ 観光・旅行</option>
                              <option value="youtube">▶️ YouTube動画・チャンネル</option>
                              <option value="culture">🎭 文化・エンタメ</option>
                              <option value="sports">⚽ スポーツ</option>
                              <option value="other">📱 その他</option>
                            </optgroup>
                          </select>
                          <input
                            type="text"
                            value={reclassifyReason}
                            onChange={(e) => setReclassifyReason(e.target.value)}
                            placeholder="変更理由 (任意)"
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-warm-gray-600 rounded text-sm dark:bg-warm-gray-700 dark:text-gray-100"
                          />
                          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-r p-2 text-xs text-blue-900 dark:text-blue-200">
                            💡 <strong>2つの保存方法があります:</strong>
                            <ul className="list-disc list-inside mt-1 space-y-0.5">
                              <li><strong>この1件を変更</strong> — 今見ている広告だけを修正します</li>
                              <li><strong>この広告主の全件を変更</strong> — 同じ広告主のすべての広告を一括修正し、今後のAI分類にも反映します（推奨）</li>
                            </ul>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={handleReclassify}
                              disabled={reclassifying || !newCategory || newCategory === selectedAd.category}
                              className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {reclassifying ? '保存中...' : '✓ この1件を変更'}
                            </button>
                            <button
                              onClick={handleBulkReclassifyByAdvertiser}
                              disabled={reclassifying || !newCategory || !selectedAd.advertiserDomain}
                              className="px-4 py-1.5 bg-fuchsia-600 text-white rounded text-sm font-semibold hover:bg-fuchsia-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              title={(() => {
                                const adv = selectedAd.enrichedAdvertiserName || selectedAd.advertiserName;
                                const dom = selectedAd.advertiserDomain;
                                if (!dom) return 'advertiser_domain が無い広告は対象外';
                                if (adv) return `「${adv}」（${dom} のチャンネル/ページ）の全件を一括で変更`;
                                return `「${dom}」 の全件を一括で変更`;
                              })()}
                            >
                              ⚡ {(() => {
                                const adv = selectedAd.enrichedAdvertiserName || selectedAd.advertiserName;
                                if (adv) return `「${adv}」 の全件を変更`;
                                return 'この広告主の全件を変更';
                              })()}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Classification History */}
                      {classificationHistory.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-100">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">分類変更履歴</div>
                          <div className="space-y-1">
                            {classificationHistory.map((h: any) => (
                              <div key={h.id} className="text-xs text-gray-600 dark:text-gray-400">
                                <span>{getCategoryLabel(h.oldCategory) || '未分類'}</span>
                                <span className="mx-1">→</span>
                                <span className="font-semibold">{getCategoryLabel(h.newCategory)}</span>
                                <span className="text-gray-400 ml-1">({h.changedBy})</span>
                                <span className="text-gray-400 ml-1">{new Date(h.changedAt).toLocaleString('ja-JP')}</span>
                                {h.changeReason && <span className="text-gray-500 italic ml-1">「{h.changeReason}」</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Source Country */}
                    {selectedAd.sourceCountry && (
                      <div className="bg-white dark:bg-warm-gray-700 rounded p-3 border border-purple-100 dark:border-warm-gray-600">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">広告元の国 / Source Country</div>
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {selectedAd.sourceCountryCode === 'JP' ? '🇯🇵 ' : ''}{selectedAd.sourceCountry}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {selectedAd.sourceCountryCode === 'JP' ? '日本国内' : '海外 (Overseas)'}
                        </div>
                      </div>
                    )}

                    {/* Enrichment Status */}
                    {selectedAd.enrichmentStatus && (
                      <div className="bg-white dark:bg-warm-gray-700 rounded p-3 border border-purple-100 dark:border-warm-gray-600">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">エンリッチメント / Enrichment</div>
                        {(() => {
                          const badge = getEnrichmentStatusBadge(selectedAd.enrichmentStatus);
                          return badge ? (
                            <div className={`inline-flex items-center px-3 py-1 rounded font-semibold text-sm ${badge.color}`}>
                              {badge.label}
                            </div>
                          ) : (
                            <div className="inline-flex items-center px-3 py-1 rounded font-semibold text-sm bg-gray-100 text-gray-600">
                              ⏳ 保留中
                            </div>
                          );
                        })()}
                        {/* Why this row was skipped/failed. Most 'skipped' rows are tracking
                            beacons or API telemetry with no advertiser content to enrich, so
                            without the reason the status alone reads as a processing gap. */}
                        {selectedAd.lastEnrichmentError && selectedAd.enrichmentStatus !== 'enriched' && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 break-all">
                            <span className="font-semibold">{t('capturedAds.enrichmentReason', '理由')}: </span>
                            {selectedAd.lastEnrichmentError}
                          </div>
                        )}
                        {selectedAd.enrichmentSource && (
                          <div className="text-xs text-gray-500 mt-1">
                            ソース: {selectedAd.enrichmentSource === 'youtube_api' ? 'YouTube Data API' :
                                     selectedAd.enrichmentSource === 'facebook_api' ? 'Facebook Graph API' :
                                     selectedAd.enrichmentSource === 'twitter_api' ? 'X (Twitter) API' :
                                     selectedAd.enrichmentSource === 'og_scraping' ? 'OG Scraping' :
                                     selectedAd.enrichmentSource}
                          </div>
                        )}
                        {selectedAd.enrichedAt && (
                          <div className="text-xs text-gray-400 mt-1">
                            処理日時: {new Date(selectedAd.enrichedAt).toLocaleString('ja-JP')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Additional Enrichment Info */}
                  {selectedAd.enrichmentStatus === 'enriched' && (
                    <div className="bg-white dark:bg-warm-gray-700 rounded p-3 border border-purple-100 dark:border-warm-gray-600">
                      <div className="text-xs text-gray-500 mb-2">エンリッチ済みデータ / Enriched Data</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">広告主:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.enrichedAdvertiserName ? '✓' : '－'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">タイトル:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.enrichedAdTitle ? '✓' : '－'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">説明:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.enrichedAdDescription ? '✓' : '－'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">サムネイル:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.enrichedThumbnailUrl ? '✓' : '－'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">LP情報:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.enrichedLandingPageUrl ? '✓' : '－'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">メタデータ:</span>
                          <span className="ml-2 font-semibold text-gray-700 dark:text-gray-300">
                            {selectedAd.platformMetadata ? '✓' : '－'}
                          </span>
                        </div>
                      </div>
                      {selectedAd.enrichmentConfidence && (
                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                          全体信頼度: <span className="font-semibold">{selectedAd.enrichmentConfidence}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Failed/Skipped Reason (if applicable) */}
                  {(selectedAd.enrichmentStatus === 'failed' || selectedAd.enrichmentStatus === 'skipped') && selectedAd.meta?.enrichment_error && (
                    <div className="bg-red-50 rounded p-3 border border-red-200">
                      <div className="text-xs text-red-700 dark:text-red-300 font-semibold mb-1">エラー詳細</div>
                      <div className="text-xs text-red-600">{selectedAd.meta.enrichment_error}</div>
                    </div>
                  )}

                  {/* Classification Evidence (for gambling and other classified ads) */}
                  {selectedAd.category && (
                    <div className="bg-white rounded p-4 border border-purple-100">
                      <div className="text-sm font-semibold text-gray-700 mb-3">
                        分類根拠・エビデンス / Classification Evidence
                      </div>
                      <div className="space-y-2 text-xs">
                        {/* Advertiser Info */}
                        {(selectedAd.enrichedAdvertiserName || selectedAd.advertiserName) && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">広告主:</span>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                              {selectedAd.enrichedAdvertiserName || selectedAd.advertiserName}
                            </span>
                          </div>
                        )}
                        {/* Ad Title */}
                        {(selectedAd.enrichedAdTitle || selectedAd.adTitle) && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">広告タイトル:</span>
                            <span className="text-gray-800 dark:text-gray-100">
                              {selectedAd.enrichedAdTitle || selectedAd.adTitle}
                            </span>
                          </div>
                        )}
                        {/* Ad Description */}
                        {(selectedAd.enrichedAdDescription || selectedAd.adDescription) && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">広告内容:</span>
                            <span className="text-gray-800 break-all">
                              {(selectedAd.enrichedAdDescription || selectedAd.adDescription || '').substring(0, 300)}
                              {(selectedAd.enrichedAdDescription || selectedAd.adDescription || '').length > 300 ? '...' : ''}
                            </span>
                          </div>
                        )}
                        {/* Advertiser Domain */}
                        {selectedAd.advertiserDomain && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">ドメイン:</span>
                            <span className="font-mono text-gray-800 dark:text-gray-100">{selectedAd.advertiserDomain}</span>
                          </div>
                        )}
                        {/* Landing Page URL */}
                        {(selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl) && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">リンク先:</span>
                            <a
                              href={selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || ''}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline break-all"
                            >
                              {(selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || '').substring(0, 100)}
                              {(selectedAd.enrichedLandingPageUrl || selectedAd.advertiserUrl || '').length > 100 ? '...' : ''}
                            </a>
                          </div>
                        )}
                        {/* Landing Page Title */}
                        {selectedAd.enrichedLandingPageTitle && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">LP タイトル:</span>
                            <span className="text-gray-800 dark:text-gray-100">{selectedAd.enrichedLandingPageTitle}</span>
                          </div>
                        )}
                        {/* Source Country */}
                        {selectedAd.sourceCountry && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 min-w-[80px]">広告元の国:</span>
                            <span className={`font-semibold ${selectedAd.sourceCountryCode === 'JP' ? 'text-gray-800' : 'text-orange-700'}`}>
                              {selectedAd.sourceCountryCode === 'JP' ? '🇯🇵 ' : ''}{selectedAd.sourceCountry}
                              {selectedAd.sourceCountryCode !== 'JP' && ' (海外)'}
                            </span>
                          </div>
                        )}
                        {/* Detection Rule */}
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[80px]">検出ルール:</span>
                          <span className="font-mono text-gray-600">{selectedAd.ruleName}</span>
                        </div>
                        {/* Platform */}
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[80px]">プラットフォーム:</span>
                          <span className="text-gray-800 dark:text-gray-100">{selectedAd.platform}</span>
                        </div>
                        {/* Detection URL */}
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[80px]">検出URL:</span>
                          <span className="font-mono text-gray-600 break-all text-[10px]">
                            {selectedAd.url.substring(0, 150)}
                            {selectedAd.url.length > 150 ? '...' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* URL */}
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">キャプチャURL</div>
                <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded p-3 font-mono text-xs break-all text-gray-700">
                  {selectedAd.url}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
